import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { triggerWeekCalculation } from '../services/schedulingJobService';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { useAppDispatch } from '../store/hooks';
import { getApiErrorMessage } from '../utils/errorMessageUtils';

export interface UseWeekCalculationResult {
  // The week awaiting confirmation, or null when no dialog is open.
  pendingWeekNumber: number | null;
  isTriggering: boolean;
  requestCalculation: (weekNumber: number) => void;
  cancelCalculation: () => void;
  confirmCalculation: () => Promise<void>;
}

/**
 * Drives the "recalculate this week" confirmation flow: holds the week the user
 * clicked, and queues a scheduling job on the backend once they confirm.
 *
 * `onCalculationQueued` fires after a successful trigger so the caller can show the week as
 * calculating at once, instead of leaving the old status icon up until the next poll.
 */
export function useWeekCalculation(
  locationId: string | undefined,
  scenarioId: string | undefined,
  onCalculationQueued?: (weekNumber: number) => void
): UseWeekCalculationResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [pendingWeekNumber, setPendingWeekNumber] = useState<number | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);

  const requestCalculation = useCallback((weekNumber: number) => {
    setPendingWeekNumber(weekNumber);
  }, []);

  const cancelCalculation = useCallback(() => {
    setPendingWeekNumber(null);
  }, []);

  const confirmCalculation = useCallback(async () => {
    if (pendingWeekNumber === null || !locationId || !scenarioId || isTriggering) return;
    setIsTriggering(true);
    try {
      await triggerWeekCalculation(locationId, scenarioId, pendingWeekNumber);
    } catch (e) {
      dispatch(showError({ message: getApiErrorMessage(e, t('gantt-recalculate-week-error')) }));
      return;
    } finally {
      setIsTriggering(false);
    }

    // The job is queued. Everything below is local bookkeeping and stays outside the try on purpose:
    // if it threw, the planner would be told the calculation could not be started for a solve the
    // backend already accepted, and the retry would queue a second one. Closing the dialog comes
    // first for the same reason — it is unconditionally correct once the backend has accepted, so it
    // must not be skipped by a subscriber that throws.
    setPendingWeekNumber(null);
    onCalculationQueued?.(pendingWeekNumber);
  }, [pendingWeekNumber, locationId, scenarioId, isTriggering, dispatch, t, onCalculationQueued]);

  return {
    pendingWeekNumber,
    isTriggering,
    requestCalculation,
    cancelCalculation,
    confirmCalculation
  };
}
